import React, { useState } from 'react';
import { Button, Flex, Tag, Text } from 'core/components';
import { FiSend } from 'react-icons/fi';
import styled from 'styled-components';
import { AI_DISCLAIMER_SHORT, MAX_VISIBLE_SUGGESTIONS } from '../constants';

const SuggestionTag = styled(Tag)`
  &.bp4-minimal {
    .bp4-icon {
      margin-right: 8px;

      svg {
        fill: ${(props) => props.theme.colors.muted} !important;
      }
    }
  }
`;

const KaizenCopilotSuggestions = ({ suggestions, onSuggestionClick, activeChatContext }) => {
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);

  // Group suggestions from activeChatContext by type, preserving context objects
  const contextSuggestionsByType = {};
  const contextsByType = {};
  if (activeChatContext) {
    Array.from(activeChatContext).forEach((context) => {
      if (context.suggestions && context.type) {
        if (!contextSuggestionsByType[context.type]) {
          contextSuggestionsByType[context.type] = [];
          contextsByType[context.type] = context; // Store the context object for display name
        }
        contextSuggestionsByType[context.type].push(...context.suggestions);
      }
    });
  }

  // Filter out context suggestions from the main suggestions array
  const contextSuggestionsFlat = Object.values(contextSuggestionsByType).flat();
  const generalSuggestions = suggestions.filter((suggestion) => !contextSuggestionsFlat.includes(suggestion));

  // Get all suggestions combined
  const allContextSuggestions = Object.values(contextSuggestionsByType).flat();
  const hasActiveContext = allContextSuggestions.length > 0;

  // Only count general suggestions toward total when they would actually be shown (no active context)
  const totalSuggestions = (hasActiveContext ? 0 : generalSuggestions.length) + allContextSuggestions.length;
  const shouldShowToggle = totalSuggestions > MAX_VISIBLE_SUGGESTIONS;

  // Limit suggestions if showAllSuggestions is false
  const visibleGeneralSuggestions = showAllSuggestions
    ? generalSuggestions
    : generalSuggestions.slice(0, Math.max(0, MAX_VISIBLE_SUGGESTIONS - allContextSuggestions.length));

  const visibleContextSuggestionsByType = {};
  if (showAllSuggestions) {
    Object.assign(visibleContextSuggestionsByType, contextSuggestionsByType);
  } else {
    let remainingSlots = MAX_VISIBLE_SUGGESTIONS - visibleGeneralSuggestions.length;
    Object.entries(contextSuggestionsByType).forEach(([type, typeSuggestions]) => {
      if (remainingSlots > 0) {
        visibleContextSuggestionsByType[type] = typeSuggestions.slice(0, remainingSlots);
        remainingSlots -= visibleContextSuggestionsByType[type].length;
      }
    });
  }

  // Shared props for suggestion tags
  const tagProps = {
    interactive: true,
    fontWeight: 400,
    minimal: true,
    alignText: 'left',
    p: '4px 10px',
    large: true,
    style: { alignSelf: 'flex-start' }
  };

  const renderSuggestionTag = (suggestion) => (
    <SuggestionTag {...tagProps} icon={FiSend} key={suggestion} onClick={() => onSuggestionClick(suggestion)}>
      {suggestion}
    </SuggestionTag>
  );

  const showGeneralSuggestions = !hasActiveContext;

  return (
    <Flex flexDirection="column" gap="8px" p={2} height="100%" justifyContent="flex-end">
      {/* General suggestions */}
      {showGeneralSuggestions && visibleGeneralSuggestions.length > 0 && (
        <>
          <Text small fontWeight="medium" mb="4px">
            Suggestions
          </Text>
          {visibleGeneralSuggestions.map(renderSuggestionTag)}
        </>
      )}

      {/* Context-based suggestions grouped by type */}
      {Object.entries(visibleContextSuggestionsByType).map(([type, typeSuggestions]) => (
        <React.Fragment key={type}>{typeSuggestions.map(renderSuggestionTag)}</React.Fragment>
      ))}

      {/* Show more/less toggle */}
      {shouldShowToggle && (
        <Button
          minimal
          small
          text={
            showAllSuggestions
              ? 'Show fewer suggestions'
              : `Show ${totalSuggestions - MAX_VISIBLE_SUGGESTIONS} more suggestions`
          }
          onClick={() => setShowAllSuggestions(!showAllSuggestions)}
          style={{ alignSelf: 'center' }}
        />
      )}

      <Text small muted textAlign="center" mt={3}>
        {AI_DISCLAIMER_SHORT}
      </Text>
    </Flex>
  );
};

export default KaizenCopilotSuggestions;
